import React, { useCallback, useEffect, useState } from 'react';
import { CleaningPreview } from '../domain/quality/dataQualityEngine';
import { apiService } from '../domain/platform/apiService';

interface DataCleaningAssistantProps {
  datasetId: string;
  onApplyCleanedData: (cleanedData: any[]) => void;
}

const DataCleaningAssistant: React.FC<DataCleaningAssistantProps> = ({ datasetId, onApplyCleanedData }) => {
  const [preview, setPreview] = useState<CleaningPreview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isApplying, setIsApplying] = useState(false);

  const loadPreview = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiService.getCleaningPreview(datasetId);
      setPreview(response.preview);
    } catch (error) {
      console.error('Failed to load cleaning preview', error);
      setPreview(null);
    } finally {
      setIsLoading(false);
    }
  }, [datasetId]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const handleApply = async () => {
    if (!preview?.operations.length) return;
    setIsApplying(true);
    try {
      const response = await apiService.applyCleaning(datasetId, preview.operations);
      onApplyCleanedData(response.result.cleanedRows);
      await loadPreview();
    } catch (error) {
      console.error('Failed to apply cleaning', error);
      alert('Cleaning could not be applied. Please retry.');
    } finally {
      setIsApplying(false);
    }
  };

  if (isLoading || !preview) {
    return (
      <section className="cleaning-assistant panel-surface">
        <div className="section-header">
          <h3>Data Cleaning Assistant</h3>
          <p>Loading cleanup preview...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="cleaning-assistant panel-surface">
      <div className="section-header">
        <h3>Data Cleaning Assistant</h3>
        <p>Preview-first cleanup with auditable operations and non-destructive versioning.</p>
      </div>

      <div className="quality-grid">
        <article className="quality-card">
          <h4>Detected Issues</h4>
          {preview.issues.length === 0 && <p className="muted">No quality issues detected in this sample.</p>}
          {preview.issues.map((issue) => (
            <div key={issue.id} className={`quality-issue severity-${issue.severity}`}>
              <div className="issue-headline">{issue.description}</div>
              <div className="issue-meta">
                <span>{issue.affectedRows} rows</span>
                <span>{issue.citation.label}</span>
              </div>
            </div>
          ))}
        </article>

        <article className="quality-card">
          <h4>Proposed Operations</h4>
          {preview.operations.length === 0 && <p className="muted">No cleanup needed.</p>}
          {preview.operations.map((operation) => (
            <div key={operation.id} className="quality-operation">
              <strong>{operation.type.replace('_', ' ')}</strong>
              <p>{operation.description}</p>
            </div>
          ))}
          <button className="primary-button" onClick={handleApply} disabled={!preview.operations.length || isApplying}>
            {isApplying ? 'Applying...' : 'Apply Safe Cleanup'}
          </button>
          <p className="muted">Impacted rows (preview): {preview.impactedRows}</p>
        </article>
      </div>

      <div className="sample-grid">
        <article className="sample-card">
          <h4>Before Sample</h4>
          <pre>{JSON.stringify(preview.beforeSample, null, 2)}</pre>
        </article>
        <article className="sample-card">
          <h4>After Sample</h4>
          <pre>{JSON.stringify(preview.afterSample, null, 2)}</pre>
        </article>
      </div>
    </section>
  );
};

export default DataCleaningAssistant;
